import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow 
} from "@/components/UI/table";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/UI/card";
import { Button } from "@/components/UI/button";
import { Input } from "@/components/UI/input";
import { 
  Download, 
  RefreshCw, 
  Calendar, 
  ChevronLeft,
  FileSpreadsheet,
  Filter,
  Plus,
  Edit,
  Trash2,
  Search
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/UI/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/UI/select";
import { useToast } from "@/components/UI/use-toast";
import { misApi } from '@/services/misApi';
import RevenueDetailView from "@/components/RevenueDetailView";

const ProfitLoss = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(searchParams.get('month') || 'Jan 2025');
  const [activeTab, setActiveTab] = useState('pl');
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for cell editing
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // State for category management
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    category_type: 'regular',
    sort_order: 500
  });
  
  // State for detail view
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Refs for sticky scrolling
  const tableContainerRef = useRef(null);
  
  // Fetch available months on component mount
  useEffect(() => {
    const fetchAvailableMonths = async () => {
      try {
        const response = await misApi.getAvailableMonths();
        const months = response.data.data || [];
        
        setAvailableMonths(months);
        
        // Set default month if not in URL params
        if (!searchParams.get('month') && months.length > 0) {
          setSelectedMonth(months[0]);
        }
      } catch (error) {
        console.error("Error fetching available months:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Could not fetch available months",
          duration: 3000,
        });
      }
    };
    
    fetchAvailableMonths();
  }, [searchParams, toast]);
  
  // Fetch profit loss data whenever the month changes
  useEffect(() => {
    if (selectedMonth) {
      fetchProfitLossData();
    }
  }, [selectedMonth]);
  
  // Apply sticky header and first column CSS
  useEffect(() => {
    if (!tableContainerRef.current) return;

    // Apply our custom styles using CSS directly
    const style = document.createElement('style');
    style.textContent = `
      .sticky-header-cell {
        position: sticky;
        top: 0;
        z-index: 10;
        background-color: #f9fafb; /* gray-50 */
      }
      
      .sticky-first-col {
        position: sticky;
        left: 0;
        z-index: 9;
      }
      
      .sticky-first-col.sticky-header-cell {
        z-index: 11;
      }
      
      .sticky-header-row {
        position: sticky;
        top: 0;
        z-index: 10;
      }
      
      /* Set the background color based on cell type */
      .bg-header {
        background-color: #f9fafb; /* gray-50 */
      }
      
      .bg-white {
        background-color: white;
      }
      
      .bg-black {
        background-color: black;
        color: white;
      }
      
      .bg-yellow {
        background-color: #fef9c3; /* yellow-100 */
      }
      
      .gradient-header {
        background: linear-gradient(to right, #3b82f6, #10b981);
        color: white;
      }
      
      /* Remove all horizontal borders from table */
      table {
        border-collapse: collapse;
      }
      
      table tr {
        border-top: none;
        border-bottom: none;
      }
      
      table th, table td {
        border-top: none;
        border-bottom: none;
      }
      
      /* Only keep right borders for column separation */
      .border-r-only {
        border-right: 1px solid #e5e7eb;
        border-top: none;
        border-bottom: none;
        border-left: none;
      }
      
      /* Styling for top header border only */
      .header-border-only {
        border-top: 1px solid #e5e7eb;
        border-bottom: none;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  useEffect(() => {
    if (selectedMonth) {
      fetchProfitLossData();
    }
  }, [selectedMonth]);
  
  
  // In fetchProfitLossData:
  const fetchProfitLossData = async () => {
    setLoading(true);
    try {
      const response = await misApi.getProfitLossReport(selectedMonth);
      console.log("API Response for Profit Loss Data:", response.data.data);
      
      // Handle different response formats
      if (response.data.data) {
        // If data.sections exists, use it directly
        if (response.data.data.sections) {
          setReportData(response.data.data);
        } 
        // Otherwise transform flat data into expected structure
        else if (Array.isArray(response.data.data)) {
          const transformedData = {
            sections: response.data.data.map(row => ({
              id: row.category_id,
              name: row.category_name,
              isHeader: row.category_type === 'header',
              isTotal: row.category_type === 'total',
              sort_order: row.sort_order,
              mtd: {
                actual: row.mtd_actual,
                accum_system: row.mtd_accum_system,
                budget: row.mtd_budget,
                variance: row.mtd_variance,
                last_year: row.mtd_last_year,
                last_report: null
              },
              ytd: {
                actual: row.ytd_actual,
                budget: row.ytd_budget,
                variance: row.ytd_variance,
                last_year: row.ytd_last_year,
                last_report: null
              }
            }))
          };
          setReportData(transformedData);
        } else {
          // Fallback to empty sections array
          setReportData({ sections: [] });
        }
      } else {
        setReportData({ sections: [] });
      }
    } catch (error) {
      console.error("Error fetching profit/loss data:", error);
      toast({
        variant: "destructive",
        title: "Failed to load data",
        description: error.response?.data?.message || "Could not load profit and loss data",
        duration: 3000,
      });
      // Set empty data on error
      setReportData({ sections: [] });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle month change
  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    // Update URL params without navigation
    const params = new URLSearchParams(searchParams);
    params.set('month', month);
    navigate(`/MIS/profit-loss?${params.toString()}`, { replace: true });
  };
  
  // Handle export to Excel
  const handleExportToExcel = async () => {
    try {
      const response = await misApi.exportProfitLoss(selectedMonth);
      
      // Create a download link for the Excel file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `profit_loss_${selectedMonth.replace(' ', '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "Export Successful",
        description: "Report has been exported to Excel",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: "Could not export report to Excel",
        duration: 3000,
      });
    }
  };
  
  // Navigation functions
  const handleBackToDashboard = () => {
    navigate('/MIS');
  };


  
  // Cell editing functions
  const handleCellDoubleClick = (section, period, field, value) => {
    // Only allow specific cell editing for non-header rows
    if (section.isHeader || 
        section.isTotal || 
        section.name === "GOP%" || 
        section.name === "NOP%" ||
        field === 'variance' ||
        section.name === "REVENUE" || 
        section.name === "TOTAL DIRECT COST" || 
        section.name === "TOTAL GENERAL & ADMINISTRATIVE EXPENSES" || 
        section.name === "TOTAL OPERATING PROFIT" || 
        section.name === "TOTAL NET PROFIT FOR THE YEAR" ||
        section.name === "Total Headcount" ||
        section.name === "TOTAL GROSS PROFIT") {
      return;
    }
    
    setEditingCell({
      sectionId: section.id,
      period,
      field
    });
    
    // Set current value for editing (remove parentheses if present)
    let editVal = value === null || value === undefined ? '' : value.toString();
    if (typeof value === 'number') {
      editVal = Math.abs(value).toString();
    } else if (editVal.startsWith('(') && editVal.endsWith(')')) {
      editVal = editVal.substring(1, editVal.length - 1).replace(/,/g, '');
    }
    setEditValue(editVal);
  };

  const fieldMappings = {
    // MTD fields
    'mtd_actual': 'mtd_actual',
    'mtd_accum_system': 'mtd_accum_system',
    'mtd_last_report': 'mtd_last_report',
    'mtd_budget': 'mtd_budget',
    'mtd_variance': 'mtd_variance',
    'mtd_last_year': 'mtd_last_year',
    
    // YTD fields
    'ytd_actual': 'ytd_actual',
    'ytd_budget': 'ytd_budget',
    'ytd_variance': 'ytd_variance',
    'ytd_last_report': 'ytd_last_report',
    'ytd_last_year': 'ytd_last_year'
  };
  
  const handleCellEdit = async () => {
    if (!editingCell) return;
    
    try {
      const { sectionId, period, field } = editingCell;
      const numericValue = parseFloat(editValue);
      
      if (isNaN(numericValue)) {
        toast({
          variant: "destructive",
          title: "Invalid Value",
          description: "Please enter a valid number",
          duration: 3000,
        });
        return;
      }
      
      // Prepare the update data
      const updateData = {
        category_id: sectionId,
        month: selectedMonth
      };
      
      // Create field key and map it to the correct backend field
      // Create field key correctly combining period and field
      const fieldKey = `${period}_${field}`;
      
      // Debug what's being passed to the mappings
      console.log('Trying to map field:', fieldKey);
      console.log('Available mappings:', fieldMappings);
      
      const mappedFieldKey = fieldMappings[fieldKey];
      
      if (!mappedFieldKey) {
        console.error('No mapping found for field:', fieldKey);
        toast({
          variant: "destructive",
          title: "Update Error",
          description: `No field mapping for ${fieldKey}`,
          duration: 3000,
        });
        return;
      }
      
      // Set the specific field being updated
      updateData[mappedFieldKey] = numericValue;
      
      // Debug the final update payload
      console.log('Updating field:', mappedFieldKey, 'with value:', numericValue);
      console.log('Full update payload:', updateData);
      
      // Call the API to update the data
      await misApi.updateProfitLossData(updateData);
      
      toast({
        title: "Value Updated",
        description: "Financial data has been updated successfully",
        duration: 3000,
      });
      
      // Reset editing state
      setEditingCell(null);
      setEditValue('');
      
      // Refresh data
      await fetchProfitLossData();
      
    } catch (error) {
      console.error("Error updating cell value:", error);
      toast({
        variant: "destructive",
        title: "Failed to Update Value",
        description: error.response?.data?.message || "Could not update value",
        duration: 3000,
      });
    }
  };
  
  // Handle category management
  const handleAddCategory = async () => {
    try {
      if (!newCategory.name) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Category name is required",
          duration: 3000,
        });
        return;
      }
      
      await misApi.addCategory(newCategory);
      
      toast({
        title: "Category Added",
        description: `Category "${newCategory.name}" has been added successfully`,
        duration: 3000,
      });
      
      // Reset form and close modal
      setNewCategory({
        name: '',
        category_type: 'regular',
        sort_order: 500
      });
      setIsAddingCategory(false);
      
      // Refresh data
      await fetchProfitLossData();
      
    } catch (error) {
      console.error("Error adding category:", error);
      toast({
        variant: "destructive",
        title: "Failed to Add Category",
        description: error.response?.data?.message || "Could not add category",
        duration: 3000,
      });
    }
  };
  
  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (window.confirm(`Are you sure you want to delete the category "${categoryName}"?`)) {
      try {
        await misApi.deleteCategory(categoryId);
        
        toast({
          title: "Category Deleted",
          description: `Category "${categoryName}" has been deleted successfully`,
          duration: 3000,
        });
        
        // Refresh data
        await fetchProfitLossData();
        
      } catch (error) {
        console.error("Error deleting category:", error);
        toast({
          variant: "destructive",
          title: "Failed to Delete Category",
          description: error.response?.data?.message || "Could not delete category",
          duration: 3000,
        });
      }
    }
  };
  

// Handle row double click for detail view
const handleRowDoubleClick = (section) => {
  // Skip only specific categories that should not be clickable
  if (section.name === "GOP%" || 
      section.name === "NOP%") {
    return;
  }
  
  // Log the section being clicked for debugging
  console.log("Double-clicked on section:", section);
  
  // Map sections to their corresponding category IDs
  const categoryMap = {
    // Revenue and Income categories
    "REVENUE": 1,
    "Other Income": 2,
    
    // Direct Cost categories
    "Direct Payroll": 2,
    "Direct Expenses": 3,
    "Materials": 4,
    "Subcontractor Charges": 5,
    "Transportation & Fuel": 6,
    "Freight and Custom Duty": 7,
    "Prov for Employees End of Service Benefits-Direct": 8,
    "Dep on Property and Equipment (Projects)-Direct": 9,
    "Other Direct Expenses": 10,
    "Rental Labors": 11,
    "Work in Progress": 12,
    
    // G&A categories
    "General & Administrative Expenses": 16,
    "Indirect Payroll": 17,
    "Other Administration Expenses": 18,
    "Employees Allowances": 19,
    "Employees Allowances (Leave Salaries,Accom,EOS,Car,Bonus)": 19,
    "Motor Vehicle Petrol and Maintenance": 20,
    "Professional and Government Fees": 21,
    "Licenses and Visa Fees": 22,
    "Rent": 23,
    "Marketing": 24,
    "Marketing (Clients Inquiries and Tender Costs)": 24,
    "Insurance": 25,
    "Travel & Entertainment (Tickets)": 26,
    "Telephone": 27,
    "Depreciation of Property and Equipment-Indirect": 28,
    "Printing & Stationery": 29,
    "Electricity & Water": 30,
    "Office Supplies": 31,
    "Depreciation of Right to use asset": 32,
    "Repairs & Maintenance & Uniforms & IT": 33,
    "Prov for Employees End of Service Benefits-Indirect": 34,
    "Other General & Admin Expenses": 35,
    "Impairment of Trade Receivables": 36,
    "Impairment of Retention Receivables": 37,
    "Loss on Liquidated Bank Guarantees": 38,
    
    // Other categories and totals
    "TOTAL DIRECT COST": 13,
    "TOTAL GROSS PROFIT": 14,
    "TOTAL G&A EXPENSES": 39,
    "TOTAL OPERATING PROFIT": 40,
    "Borrowings Costs": 41,
    "TOTAL NET PROFIT FOR THE YEAR": 43,
    "Direct Headcount": 45,
    "Indirect Headcount": 46,
    "Total Headcount": 47
  };
  
  // Get the category ID from the map, or use the section's ID if not found
  const categoryId = categoryMap[section.name] || section.id;
  
  console.log(`Mapped category ID for "${section.name}": ${categoryId}`);
  
  // Set the selected category with the correct ID
  setSelectedCategory({
    ...section,
    id: categoryId
  });
  
  // Provide feedback to the user
  toast({
    title: "Loading details",
    description: `Viewing details for ${section.name}`,
    duration: 2000,
  });
};

  // Handle closing detail view
  const handleCloseDetailView = () => {
    setSelectedCategory(null);
  };

  // Handle data updated from detail view
  const handleDataUpdated = async () => {
    await fetchProfitLossData();
  };
  
  // Formatting functions
  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    
    if (typeof value === 'number') {
      // For percentage values (usually variance)
      if (Math.abs(value) < 1) {
        return `${(value * 100).toFixed(1)}%`;
      }
      
      // Regular number formatting with commas
      // Only use parentheses for negative values
      if (value < 0) {
        return `(${Math.abs(value).toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })})`;
      }
      
      // For zero values, just show 0 
      if (value === 0) {
        return "0";
      }
      
      // For positive values, don't use parentheses
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
    
    return value;
  };
  
  // Special formatting for specific rows (percentages)
  const formatSpecialValue = (value, rowName) => {
    if (rowName === "GOP%" || rowName === "NOP%") {
      if (value === null || value === undefined) return "-";
      if (typeof value === 'number') {
        return `${(value * 100).toFixed(1)}%`;
      }
      return value;
    }
    
    return formatValue(value);
  };
  
  // Filter sections based on search query
  const filteredSections = reportData?.sections.filter(section => 
    !searchQuery || section.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];
  
  // Loading skeleton
  const renderTableSkeleton = () => (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-4">
        <div className="h-8 w-64 bg-gray-200 animate-pulse rounded"></div>
        <div className="h-10 w-32 bg-gray-200 animate-pulse rounded"></div>
      </div>
      
      <div className="border rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4">
          <div className="h-6 col-span-1 bg-gray-200 animate-pulse rounded"></div>
          <div className="h-6 col-span-11 bg-gray-200 animate-pulse rounded"></div>
        </div>
        
        {[...Array(10)].map((_, i) => (
          <div key={i} className="grid grid-cols-12 gap-4 p-4">
            <div className="h-6 col-span-1 bg-gray-200 animate-pulse rounded"></div>
            {[...Array(11)].map((_, j) => (
              <div key={j} className="h-6 col-span-1 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
  
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackToDashboard}
            className="text-gray-600"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Button>
          <h1 className="text-xl font-semibold text-gray-800">Profit & Loss</h1>
        </div>
        
        {renderTableSkeleton()}
      </div>
    );
  }
  
  // If no data is available
  if (!reportData) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center space-x-4 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackToDashboard}
            className="text-gray-600"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Button>
          <h1 className="text-xl font-semibold text-gray-800">Profit & Loss</h1>
        </div>
        
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-12">
            <FileSpreadsheet className="h-16 w-16 text-gray-300 mb-4" />
            <h2 className="text-xl font-medium text-gray-700 mb-2">No Data Available</h2>
            <p className="text-gray-500 text-center max-w-md">
              There's no profit and loss data available for the selected period.
              Please upload a trial balance file first.
            </p>
            <Button
              className="mt-6 bg-[#C5A572] hover:bg-[#a68445]"
              onClick={handleBackToDashboard}
            >
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      {/* Header section with back button and controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackToDashboard}
            className="text-gray-600"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Button>
          <h1 className="text-xl font-semibold text-gray-800">Profit & Loss</h1>
        </div>
        
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          {/* Month Selection */}
          <Select value={selectedMonth} onValueChange={handleMonthChange}>
            <SelectTrigger className="w-[180px]">
              <Calendar className="h-4 w-4 mr-2 text-gray-500" />
              <SelectValue placeholder="Select Month" />
            </SelectTrigger>
            <SelectContent>
              {availableMonths.map((month) => (
                <SelectItem key={month} value={month}>
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchProfitLossData}
            className="text-gray-600"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          
          {/* Export Button */}
          <Button 
            size="sm"
            onClick={handleExportToExcel}
            className="bg-[#C5A572] hover:bg-[#a68445]"
          >
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </div>
      </div>
      
      {/* Main content */}
      <Tabs defaultValue="pl" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 bg-white border rounded-lg p-1">
          <TabsTrigger value="pl" className="data-[state=active]:bg-[#C5A572] data-[state=active]:text-white">
            Profit & Loss
          </TabsTrigger>
          <TabsTrigger value="bs" className="data-[state=active]:bg-[#C5A572] data-[state=active]:text-white">
            Balance Sheet
          </TabsTrigger>
          <TabsTrigger value="cf" className="data-[state=active]:bg-[#C5A572] data-[state=active]:text-white">
            Cash Flow
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="pl" className="mt-0">
          {selectedCategory ? (
            <RevenueDetailView 
              category={selectedCategory}
              month={selectedMonth}
              onClose={handleCloseDetailView}
              onDataUpdated={handleDataUpdated}
            />
          ) : (
            <Card className="shadow-sm">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-green-500 text-white py-2 px-4">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg font-semibold">
                    Profit & Loss
                  </CardTitle>
                  <span className="text-sm font-medium">{`MIS ${selectedMonth}`}</span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Search and Category Management */}
                <div className="p-2 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center space-x-2 w-64">
                    <Search className="h-4 w-4 text-gray-500" />
                    <Input 
                      placeholder="Search categories..."
                      className="h-8"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex items-center" 
                    onClick={() => setIsAddingCategory(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Category
                  </Button>
                </div>
                
                {/* Table with fixed header and first column */}
                <div 
                  className="overflow-auto max-h-[70vh]" 
                  ref={tableContainerRef}
                  style={{ position: 'relative' }}
                >
                  <table className="w-full" style={{borderCollapse: 'collapse', borderSpacing: 0}}>
                    <thead>
                      <tr>
                        <th className="sticky-header-cell sticky-first-col bg-header p-2 border-r-only text-left min-w-[200px]"style={{backgroundColor: '#c6e0b4'}}>
                          <div className="flex items-center justify-between"style={{backgroundColor: '#c6e0b4'}}>
                            <span>Particulars</span>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 opacity-70 hover:opacity-100"
                              onClick={() => setIsAddingCategory(true)}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </th>
                        
                        {/* MTD Headers */}
                        <th colSpan={6} className="sticky-header-cell gradient-header p-2 text-center border-r-only" style={{backgroundColor: '#c6e0b4'}}>MTD</th>
                        
                        {/* YTD Headers */}
                        <th colSpan={5} className="sticky-header-cell gradient-header p-2 text-center" style={{backgroundColor: '#d9d9d9'}}>YTD</th>
                      </tr>
                      
                      <tr>
                        <th className="sticky-header-cell sticky-first-col bg-header p-2 border-r-only"></th>
                        
                        {/* MTD Sub-headers */}
                        <th className="sticky-header-cell bg-header p-2 text-right" style={{backgroundColor: '#c6e0b4'}}>Actual</th>
                        <th className="sticky-header-cell p-2 text-right text-black" style={{backgroundColor: '#ffff00'}}>Accum System<br/>{selectedMonth}</th>
                        <th className="sticky-header-cell p-2 text-right text-black" style={{backgroundColor: '#ffff00'}}>Last Report<br/>MIS</th>
                        <th className="sticky-header-cell bg-header p-2 text-right" style={{backgroundColor: '#c6e0b4'}}>Budget</th>
                        <th className="sticky-header-cell bg-header p-2 text-right" style={{backgroundColor: '#c6e0b4'}}>Variance</th>
                        <th className="sticky-header-cell bg-header p-2 text-right border-r-only" style={{backgroundColor: '#c6e0b4'}}>Last Year</th>

                        {/* YTD Sub-headers */}
                        <th className="sticky-header-cell bg-header p-2 text-right" style={{backgroundColor: '#d9d9d9'}}>Actual</th>
                        <th className="sticky-header-cell bg-header p-2 text-right" style={{backgroundColor: '#d9d9d9'}}>Budget</th>
                        <th className="sticky-header-cell bg-header p-2 text-right" style={{backgroundColor: '#d9d9d9'}}>Variance</th>
                        <th className="sticky-header-cell p-2 text-right text-black" style={{backgroundColor: '#90EE90'}}>Last Report</th>
                        <th className="sticky-header-cell bg-header p-2 text-right" style={{backgroundColor: '#d9d9d9'}}>Last Year</th>
                      </tr>
                    </thead>
                    
                    <tbody>
                      {filteredSections.map((section, index) => {
                        // Determine styling based on row type
                        const isBlackRow = 
                          section.name === "REVENUE" || 
                          section.name === "TOTAL DIRECT COST" || 
                          section.name === "TOTAL GROSS PROFIT" ||
                          section.name === "TOTAL GENERAL & ADMINISTRATIVE EXPENSES" || 
                          section.name === "TOTAL OPERATING PROFIT" || 
                          section.name === "TOTAL NET PROFIT FOR THE YEAR" ||
                          section.name === "Total Headcount";
                          
                        const isYellowRow = 
                          section.name === "GOP%" || 
                          section.name === "NOP%";
                        
                        // For regular categories, use blue text
                        const isRegularCategory = !section.isHeader && !section.isTotal && 
                                                !isBlackRow && !isYellowRow;
                        
                        const cellClass = isRegularCategory ? 'text-blue-600 italic font-medium' : '';
                        
                        // Check if this category can be deleted (not system category)
                        const canDelete = isRegularCategory;
                        
                        return (
                          <tr 
                            key={index}
                            className={`h-5 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}
                            style={{borderTop: 'none', borderBottom: 'none'}}
                            onDoubleClick={() => handleRowDoubleClick(section)}
                          >
                            <td className={`sticky-first-col p-2 border-r-only ${
                              isBlackRow ? 'bg-black text-white' : 
                              isYellowRow ? 'bg-yellow-100' : 'bg-white'
                            } ${cellClass}`} style={{borderTop: 'none', borderBottom: 'none'}}>
                              <div className="flex items-center justify-between">
                                <span>{section.name}</span>
                                {canDelete && (
                                  <button
                                    className="h-5 w-5 opacity-30 hover:opacity-100 text-red-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteCategory(section.id, section.name);
                                    }}
                                    title="Delete category"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            </td>
                            
                            {/* MTD Values */}
                            <td 
                              className={`p-2 text-right ${
                                isBlackRow ? 'bg-black text-white' : 
                                isYellowRow ? 'bg-yellow-100' : ''
                              } ${cellClass}`}
                              style={{borderTop: 'none', borderBottom: 'none'}}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                handleCellDoubleClick(section, 'mtd', 'actual', section.mtd.actual);
                              }}
                            >
                              {editingCell && 
                               editingCell.sectionId === section.id && 
                               editingCell.period === 'mtd' && 
                               editingCell.field === 'actual' ? (
                                <input
                                  type="text"
                                  className="w-full h-6 p-1 text-right border rounded text-black bg-white"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={handleCellEdit}
                                  onKeyDown={(e) => e.key === 'Enter' && handleCellEdit()}
                                  autoFocus
                                />
                              ) : (
                                <span>
                                  {section.name === "GOP%" || section.name === "NOP%" 
                                    ? formatSpecialValue(section.mtd.actual, section.name)
                                    : formatValue(section.mtd.actual)}
                                </span>
                              )}
                            </td>
                            <td 
                              className={`p-2 text-right ${
                                isBlackRow ? 'bg-black text-white' : 
                                isYellowRow ? 'bg-yellow-100' : ''
                              } ${cellClass}`}
                              style={{borderTop: 'none', borderBottom: 'none'}}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                handleCellDoubleClick(section, 'mtd', 'accum_system', section.mtd.accum_system);
                              }}
                            >
                              {editingCell && 
                               editingCell.sectionId === section.id && 
                               editingCell.period === 'mtd' && 
                               editingCell.field === 'accum_system' ? (
                                <input
                                  type="text"
                                  className="w-full h-6 p-1 text-right border rounded text-black bg-white"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={handleCellEdit}
                                  onKeyDown={(e) => e.key === 'Enter' && handleCellEdit()}
                                  autoFocus
                                />
                              ) : (
                                <span>
                                  {section.name === "GOP%" || section.name === "NOP%" 
                                    ? formatSpecialValue(section.mtd.accum_system, section.name)
                                    : formatValue(section.mtd.accum_system)}
                                </span>
                              )}
                            </td>
                            <td 
                              className={`p-2 text-right ${
                                isBlackRow ? 'bg-black text-white' : 
                                isYellowRow ? 'bg-yellow-100' : ''
                              } ${cellClass}`}
                              style={{borderTop: 'none', borderBottom: 'none'}}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                handleCellDoubleClick(section, 'mtd', 'last_report', section.mtd.last_report);
                              }}
                            >
                              {editingCell && 
                               editingCell.sectionId === section.id && 
                               editingCell.period === 'mtd' && 
                               editingCell.field === 'last_report' ? (
                                <input
                                  type="text"
                                  className="w-full h-6 p-1 text-right border rounded text-black bg-white"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={handleCellEdit}
                                  onKeyDown={(e) => e.key === 'Enter' && handleCellEdit()}
                                  autoFocus
                                />
                              ) : (
                                <span>
                                  {section.name === "GOP%" || section.name === "NOP%" 
                                    ? formatSpecialValue(section.mtd.last_report, section.name)
                                    : formatValue(section.mtd.last_report)}
                                </span>
                              )}
                            </td>
                            <td 
                              className={`p-2 text-right ${
                                isBlackRow ? 'bg-black text-white' : 
                                isYellowRow ? 'bg-yellow-100' : ''
                              } ${cellClass}`}
                              style={{borderTop: 'none', borderBottom: 'none'}}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                handleCellDoubleClick(section, 'mtd', 'budget', section.mtd.budget);
                              }}
                            >
                              {editingCell && 
                               editingCell.sectionId === section.id && 
                               editingCell.period === 'mtd' && 
                               editingCell.field === 'budget' ? (
                                <input
                                  type="text"
                                  className="w-full h-6 p-1 text-right border rounded text-black bg-white"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={handleCellEdit}
                                  onKeyDown={(e) => e.key === 'Enter' && handleCellEdit()}
                                  autoFocus
                                />
                              ) : (
                                <span>
                                  {section.name === "GOP%" || section.name === "NOP%" 
                                    ? formatSpecialValue(section.mtd.budget, section.name)
                                    : formatValue(section.mtd.budget)}
                                </span>
                              )}
                            </td>
                            <td className={`p-2 text-right ${
                              isBlackRow ? 'bg-black text-white' : 
                              isYellowRow ? 'bg-yellow-100' : ''
                            } ${
                              section.mtd.variance < 0 ? 'text-red-600' : 
                              section.mtd.variance > 0 ? 'text-green-600' : ''
                            } ${cellClass}`}
                            style={{borderTop: 'none', borderBottom: 'none'}}>
                              <span>
                                {section.name === "GOP%" || section.name === "NOP%" 
                                  ? formatSpecialValue(section.mtd.variance, section.name)
                                  : formatValue(section.mtd.variance)}
                              </span>
                            </td>
                            <td 
                              className={`p-2 text-right border-r-only ${
                                isBlackRow ? 'bg-black text-white' : 
                                isYellowRow ? 'bg-yellow-100' : ''
                              } ${cellClass}`}
                              style={{borderTop: 'none', borderBottom: 'none'}}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                handleCellDoubleClick(section, 'mtd', 'last_year', section.mtd.last_year);
                              }}
                            >
                              {editingCell && 
                               editingCell.sectionId === section.id && 
                               editingCell.period === 'mtd' && 
                               editingCell.field === 'last_year' ? (
                                <input
                                  type="text"
                                  className="w-full h-6 p-1 text-right border rounded text-black bg-white"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={handleCellEdit}
                                  onKeyDown={(e) => e.key === 'Enter' && handleCellEdit()}
                                  autoFocus
                                />
                              ) : (
                                <span>
                                  {section.name === "GOP%" || section.name === "NOP%" 
                                    ? formatSpecialValue(section.mtd.last_year, section.name)
                                    : formatValue(section.mtd.last_year)}
                                </span>
                              )}
                            </td>
                            {/* YTD Values - Similar to MTD values */}
                            <td 
                              className={`p-2 text-right ${
                                isBlackRow ? 'bg-black text-white' : 
                                isYellowRow ? 'bg-yellow-100' : ''
                              } ${cellClass}`}
                              style={{borderTop: 'none', borderBottom: 'none'}}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                handleCellDoubleClick(section, 'ytd', 'actual', section.ytd.actual);
                              }}
                            >
                              {editingCell && 
                               editingCell.sectionId === section.id && 
                               editingCell.period === 'ytd' && 
                               editingCell.field === 'actual' ? (
                                <input
                                  type="text"
                                  className="w-full h-6 p-1 text-right border rounded text-black bg-white"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={handleCellEdit}
                                  onKeyDown={(e) => e.key === 'Enter' && handleCellEdit()}
                                  autoFocus
                                />
                              ) : (
                                <span>
                                  {section.name === "GOP%" || section.name === "NOP%" 
                                    ? formatSpecialValue(section.ytd.actual, section.name)
                                    : formatValue(section.ytd.actual)}
                                </span>
                              )}
                            </td>
                            <td 
                              className={`p-2 text-right ${
                                isBlackRow ? 'bg-black text-white' : 
                                isYellowRow ? 'bg-yellow-100' : ''
                              } ${cellClass}`}
                              style={{borderTop: 'none', borderBottom: 'none'}}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                handleCellDoubleClick(section, 'ytd', 'budget', section.ytd.budget);
                              }}
                            >
                              {editingCell && 
                               editingCell.sectionId === section.id && 
                               editingCell.period === 'ytd' && 
                               editingCell.field === 'budget' ? (
                                <input
                                  type="text"
                                  className="w-full h-6 p-1 text-right border rounded text-black bg-white"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={handleCellEdit}
                                  onKeyDown={(e) => e.key === 'Enter' && handleCellEdit()}
                                  autoFocus
                                />
                              ) : (
                                <span>
                                  {section.name === "GOP%" || section.name === "NOP%" 
                                    ? formatSpecialValue(section.ytd.budget, section.name)
                                    : formatValue(section.ytd.budget)}
                                </span>
                              )}
                            </td>
                            <td className={`p-2 text-right ${
                              isBlackRow ? 'bg-black text-white' : 
                              isYellowRow ? 'bg-yellow-100' : ''
                            } ${
                              section.ytd.variance < 0 ? 'text-red-600' : 
                              section.ytd.variance > 0 ? 'text-green-600' : ''
                            } ${cellClass}`}
                            style={{borderTop: 'none', borderBottom: 'none'}}>
                              <span>
                                {section.name === "GOP%" || section.name === "NOP%" 
                                  ? formatSpecialValue(section.ytd.variance, section.name)
                                  : formatValue(section.ytd.variance)}
                              </span>
                            </td>
                            <td 
                              className={`p-2 text-right ${
                                isBlackRow ? 'bg-black text-white' : 
                                isYellowRow ? 'bg-yellow-100' : ''
                              } ${cellClass}`}
                              style={{borderTop: 'none', borderBottom: 'none'}}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                handleCellDoubleClick(section, 'ytd', 'last_report', section.ytd.last_report);
                              }}
                            >
                              {editingCell && 
                               editingCell.sectionId === section.id && 
                               editingCell.period === 'ytd' && 
                               editingCell.field === 'last_report' ? (
                                <input
                                  type="text"
                                  className="w-full h-6 p-1 text-right border rounded text-black bg-white"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={handleCellEdit}
                                  onKeyDown={(e) => e.key === 'Enter' && handleCellEdit()}
                                  autoFocus
                                />
                              ) : (
                                <span>
                                  {section.name === "GOP%" || section.name === "NOP%" 
                                    ? formatSpecialValue(section.ytd.last_report, section.name)
                                    : formatValue(section.ytd.last_report)}
                                </span>
                              )}
                            </td>
                            <td 
                              className={`p-2 text-right ${
                                isBlackRow ? 'bg-black text-white' : 
                                isYellowRow ? 'bg-yellow-100' : ''
                              } ${cellClass}`}
                              style={{borderTop: 'none', borderBottom: 'none'}}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                handleCellDoubleClick(section, 'ytd', 'last_year', section.ytd.last_year);
                              }}
                            >
                              {editingCell && 
                               editingCell.sectionId === section.id && 
                               editingCell.period === 'ytd' && 
                               editingCell.field === 'last_year' ? (
                                <input
                                  type="text"
                                  className="w-full h-6 p-1 text-right border rounded text-black bg-white"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={handleCellEdit}
                                  onKeyDown={(e) => e.key === 'Enter' && handleCellEdit()}
                                  autoFocus
                                />
                              ) : (
                                <span>
                                  {section.name === "GOP%" || section.name === "NOP%" 
                                    ? formatSpecialValue(section.ytd.last_year, section.name)
                                    : formatValue(section.ytd.last_year)}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Add Category Dialog */}
                {isAddingCategory && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                      <h2 className="text-xl font-bold mb-4">Add New Category</h2>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Category Name</label>
                          <input 
                            type="text" 
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="Enter category name"
                            value={newCategory.name}
                            onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Category Type</label>
                          <select 
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            value={newCategory.category_type}
                            onChange={(e) => setNewCategory({...newCategory, category_type: e.target.value})}
                          >
                            <option value="regular">Regular</option>
                            <option value="header">Header</option>
                            <option value="total">Total</option>
                            <option value="header_total">Header Total</option>
                            <option value="percentage">Percentage</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Sort Order</label>
                          <input 
                            type="number" 
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="Enter sort order"
                            value={newCategory.sort_order}
                            onChange={(e) => setNewCategory({...newCategory, sort_order: parseInt(e.target.value) || 0})}
                          />
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end space-x-3">
                        <button 
                          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                          onClick={() => setIsAddingCategory(false)}
                        >
                          Cancel
                        </button>
                        <button 
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          onClick={handleAddCategory}
                        >
                          Add Category
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="bs">
          <Card>
            <CardContent className="pt-6 flex flex-col items-center justify-center h-64">
              <FileSpreadsheet className="h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-500 font-medium">Balance Sheet report will be implemented in the next phase.</p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="cf">
          <Card>
            <CardContent className="pt-6 flex flex-col items-center justify-center h-64">
              <FileSpreadsheet className="h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-500 font-medium">Cash Flow report will be implemented in the next phase.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      {/* Footer with info about the data */}
      <div className="text-xs text-gray-500 mt-4">
        <p className="flex items-center">
          <Calendar className="h-3 w-3 mr-1" /> 
          Report generated for period: <span className="font-medium ml-1">{selectedMonth}</span>
        </p>
        <p className="mt-1">
          Revenue calculation is based on accounts starting with '4' from transaction data.
        </p>
      </div>
    </div>
  );
};

export default ProfitLoss;